import { use, useEffect, useMemo, useState } from 'react';
import { SelectChangeEvent } from '@mui/material/Select';
import { jsonParseWithLowerCaseKeys } from 'shared/utils/string-utils';

import AudioConfiguration from 'features/residents/models/configurations/audio-configuration';
import TimeFrame from 'features/residents/models/configurations/time-frame';
import ResidentSettingsProviderContext from 'features/residents/resident-settings/providers/contexts/resident-settings-provider-context';

export enum ActiveOption {
  AlarmsOnly = 0,
  Always = 1,
}

type Props = {
  index: number;
};

type AudioScenarioConfiguration = {
  configuration: AudioConfiguration;
  timeFrameValuesChanged: (newTimeFrames: TimeFrame[] | null, newActiveAllDay: boolean) => void;
  onAlarmActiveChanged: (event: SelectChangeEvent<number>) => void;
  determineSelectedActiveOption: () => ActiveOption;
};

export const useAudioScenarioConfiguration = ({ index }: Props): AudioScenarioConfiguration => {
  const {
    detailEditState: { viewingMode, currentResidentSetting, residentSettingEditForm },
  } = use(ResidentSettingsProviderContext);
  const rawConfiguration = useMemo(
    () => currentResidentSetting?.organisationUnitScenarios[index].configuration,
    [currentResidentSetting, index],
  );

  const [configuration, setConfiguration] = useState(jsonParseWithLowerCaseKeys(rawConfiguration) as AudioConfiguration);

  const scenarioConfigurationUpdated = (newConfiguration: unknown) => {
    residentSettingEditForm.setValue(`organisationUnitScenarios.${index}.configuration`, JSON.stringify(newConfiguration), {
      shouldDirty: true,
    });
  };

  const timeFrameValuesChanged = (newTimeFrames: TimeFrame[] | null, newActiveAllDay: boolean): void => {
    const newConfig = {
      ...configuration,
      timeFrames: newTimeFrames,
      activeAllDay: newActiveAllDay,
    };
    scenarioConfigurationUpdated(newConfig);
    setConfiguration(newConfig);
  };

  const onWhenActiveAlarmChanged = (value: boolean): void => {
    const newConfig = {
      ...configuration,
      whenActiveAlarm: value,
    };
    scenarioConfigurationUpdated(newConfig);
    setConfiguration(newConfig);
  };

  useEffect(() => {
    residentSettingEditForm.clearErrors(`organisationUnitScenarios.${index}`);
    setConfiguration(jsonParseWithLowerCaseKeys(rawConfiguration) as AudioConfiguration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawConfiguration, viewingMode]);

  const onAlarmActiveChanged = (event: SelectChangeEvent<number>) => {
    const selectedValue = event.target.value as ActiveOption;
    onWhenActiveAlarmChanged(selectedValue === ActiveOption.AlarmsOnly);
  };

  const determineSelectedActiveOption = (): ActiveOption => (configuration.whenActiveAlarm ? ActiveOption.AlarmsOnly : ActiveOption.Always);

  return {
    configuration,
    timeFrameValuesChanged,
    onAlarmActiveChanged,
    determineSelectedActiveOption,
  };
};
